import './App.css';
import { useState } from 'react';
import { Toaster } from "react-hot-toast";
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {BluPaySplash } from './Pages/BlupaySplash';

// Layouts & Guards
import { AppLayout } from './components/AppLayout';
import { AdminLayout } from './components/AdminLayout';
import { AdminRoute } from './components/AdminRoute';
import { ErrorBoundary } from './components/errors/ErrorBoundary';
import { RequireAuth } from './components/auth/RequireAuth';
import { RequireRole } from './components/auth/RequireRole';
import { OPS_EXECUTION_ROLES } from './constants/roles';

// Contexts
import { AuthProvider } from './context/AuthContext';
import { TransferFlowProvider } from './context/TransferFlowContext';

// Pages & Components
import { SignUp } from './Pages/SignUp';
import { Login } from './Pages/Login';
import { ResetPassword } from './Pages/ResetPassword';
import { Transfer } from './components/transfer/Transfer';
import { CurrencyPicker } from './components/transfer/CurrencyPicker';
import { IntroductoryPage } from './Pages/Introductory';
import { Home } from './Pages/Home';
import { CreateRecipient } from './components/CreateRecipient';
import { TransactionConfirmed } from './components/transfer/TransactionSuccess';
import { TransactionSummary } from './components/transfer/TransactionSummary';
import { TransactionHistory } from './Pages/TransactionHistory';
import { TransactionDetails } from './components/TransactionDetails';
import { EditProfilePage } from './Pages/ProfileEdit';
import { EditRecipientPage } from './Pages/EditRecipient';
import { Kyc } from './Pages/KYC';
import { OpsLegExecution } from './Pages/OpsLegExecution';
import { CreateFundingSource } from './components/CreateFundingSource';
import { SmartEntry } from './Pages/SmartEntry';
import { SetupPin } from './Pages/SetUpPin';
import { Security } from './Pages/Security';
import { NotificationsPage } from './Pages/NotificationsPage';
import { FxCalculator } from './Pages/FxCalculator';
import { OnboardingFinish } from './Pages/OnboardingFinish';
import { Support } from './Pages/SupportPage';

// Admin Pages
import { AdminOverview } from './components/admin/AdminOverview';
import { AdminUsers } from './components/admin/AdminUsers';
import { AdminTransfers } from './components/admin/AdminTransfers';
import { AdminTransferDetail } from './components/admin/AdminTransferDetail';
import { AdminCompliance } from './components/admin/AdminCompliance';
import { AdminAudit } from './components/admin/AdminAudit';
import { AdminProfile } from './components/admin/AdminProfile';
import { AdminSettings } from './components/admin/AdminSettings';
import { PrivyProviderGate } from './context/PrivyProviderGate';

const queryClient = new QueryClient();

function App() {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <div className="App">

        {showSplash && (
        <BluPaySplash
          sendLabel="USD"
          receiveLabel="GHS"
          onDone={() => setShowSplash(false)}
        />
      )}
      <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <Toaster position="top-right" />
          <PrivyProviderGate>
            <AuthProvider>
              <TransferFlowProvider>
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<SmartEntry />} />
                  <Route path="/about" element={<IntroductoryPage />} />
                  <Route path="/signup" element={<SignUp />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/onboarding/finish" element={<OnboardingFinish />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/rates" element={<FxCalculator />} />

                  {/* Protected User Routes (Guarded by RequireAuth + AppLayout) */}
                  <Route element={<RequireAuth />}>
                    <Route element={<AppLayout />}>
                      <Route path="/support" element={<Support/>} />
                      <Route path="/smart-entry" element={<SmartEntry />} />
                      <Route path="/intro" element={<IntroductoryPage />} />
                      <Route path="/home" element={<Home />} />
                      <Route path="/transfer" element={<Transfer />} />
                      <Route path="/transactionsummary" element={<TransactionSummary />} />
                      <Route path="/transactionconfirmed" element={<TransactionConfirmed />} />
                      <Route path="/transactionhistory" element={<TransactionHistory />} />
                      <Route path="/transaction/:id" element={<TransactionDetails />} />
                      <Route path="/currencypicker" element={<CurrencyPicker />} />
                      <Route path="/notifications" element={<NotificationsPage />} />
                      <Route path="/fxcalculator" element={<FxCalculator />} />
                      <Route path="/profile/edit" element={<EditProfilePage />} />
                      <Route path="/createrecipient" element={<CreateRecipient />} />
                      <Route path="/editrecipient/:id" element={<EditRecipientPage />} />
                      <Route path="/kyc" element={<Kyc />} />
                      <Route path="/funding-source/new" element={<CreateFundingSource />} />
                      <Route path="/pin/setup" element={<SetupPin />} />
                      <Route path="/pin/change" element={<Security />} />
                      <Route element={<RequireRole roles={OPS_EXECUTION_ROLES} />}>
                        <Route path="/ops/legs" element={<OpsLegExecution />} />
                      </Route>
                    </Route>
                  </Route>

                  {/* Protected Admin Routes */}
                  <Route element={<AdminRoute />}>
                    <Route element={<AdminLayout />}>
                      <Route path="/admin" element={<AdminOverview />} />
                      <Route path="/admin/users" element={<AdminUsers />} />
                      <Route path="/admin/transfers" element={<AdminTransfers />} />
                      <Route path="/admin/transfers/:id" element={<AdminTransferDetail />} />
                      <Route path="/admin/compliance" element={<AdminCompliance />} />
                      <Route path="/admin/audit" element={<AdminAudit />} />
                      <Route path="/admin/profile" element={<AdminProfile />} />
                      <Route path="/admin/settings" element={<AdminSettings />} />
                    </Route>
                  </Route>

                  {/* Fallback Route */}
                  <Route path="*" element={<h1>Page Not Found</h1>} />
                </Routes>
              </TransferFlowProvider>
            </AuthProvider>
          </PrivyProviderGate>
        </QueryClientProvider>
      </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
